/* eslint-disable eag/no-css-property */
import {
  Grid,
  HStack,
  Integer,
  VStack,
  useResponsivePropValue,
  ValueByDevice,
} from 'platform/foundation';
import styled from 'styled-components';

import {ReactNode} from 'react';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

export interface GroupProps extends RequiredTestIdProps {
  children: ReactNode;
  columns?: Integer | ValueByDevice<Integer>;
  spacing?: Integer;
  direction?: 'horizontal' | 'vertical';
}

const TakeFullWidth = styled.div`
  & > div > div {
    flex: 1 1 0;
    min-width: ${({theme}) => theme.getSize(50)};
  }
`;

/**
 * Group primitive - provides consistent layout for related fields
 *
 * @example
 * // Horizontal layout with automatic wrapping
 * <Group direction="horizontal">
 *   <Field name="make" />
 *   <Field name="model" />
 *   <Field name="year" />
 * </Group>
 *
 * // Explicit grid columns
 * <Group columns={2}>
 *   <Field name="make" />
 *   <Field name="model" />
 * </Group>
 *
 * // Responsive columns - 1 on mobile, 2 on tablet+
 * <Group columns={[1, 2]}>
 *   <Field name="make" />
 *   <Field name="model" />
 * </Group>
 *
 * // Full responsive columns
 * <Group columns={[1, 2, 3, 4]}>
 *   <Field name="make" />
 *   <Field name="model" />
 *   <Field name="year" />
 *   <Field name="variant" />
 * </Group>
 *
 * // Vertical layout
 * <Group direction="vertical" spacing={4}>
 *   <Field name="power" />
 *   <Field name="volume" />
 * </Group>
 */
export function Group(props: GroupProps) {
  const {children, columns, spacing = 4, direction = 'horizontal', ...testIdProps} = props;
  const resolvedColumns = useResponsivePropValue(columns);

  // Use Grid when explicit columns are specified
  if (resolvedColumns !== undefined) {
    return (
      <Grid
        columns={resolvedColumns}
        data-testid={suffixTestId('group', testIdProps)}
        align="center"
      >
        {children}
      </Grid>
    );
  }

  if (direction === 'vertical') {
    return (
      <TakeFullWidth>
        <VStack spacing={spacing} width="100%" data-testid={suffixTestId('group', testIdProps)}>
          {children}
        </VStack>
      </TakeFullWidth>
    );
  }

  return (
    <TakeFullWidth>
      <HStack spacing={spacing} wrap width="100%" data-testid={suffixTestId('group', testIdProps)}>
        {children}
      </HStack>
    </TakeFullWidth>
  );
}
